import React from 'react';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import type { Incident } from '../../services/incidentsApi';

export interface RoutingIntegrationStatusProps {
  groupedReviewMode?: boolean;
  selectedIncident: Incident;
  getOdooStateColor: (state: string | undefined) => string;
}

const RoutingIntegrationStatus: React.FC<RoutingIntegrationStatusProps> = ({
  groupedReviewMode = false,
  selectedIncident,
  getOdooStateColor,
}) => {
  const storageStatus = selectedIncident.routing_status?.dashboard?.success === false ? 'Failed' : 'Stored';
  const webhookStatus =
    selectedIncident.routing_status?.webhook?.enabled === false
      ? 'Disabled'
      : selectedIncident.routing_status?.webhook?.success === true
        ? 'Delivered'
        : selectedIncident.routing_status?.webhook?.success === false
          ? 'Failed'
          : 'Not configured';

  return (
    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.5 }}>Work order and delivery status</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, lineHeight: 1.45 }}>
        Confirm where this incident is visible now, whether follow-up is linked to a work order, and whether downstream delivery succeeded.
        {groupedReviewMode
          ? ' Edgentik review ownership and Odoo work-order ownership are separate. Create or open a work order when external task tracking is needed.'
          : ''}
      </Typography>

      <Grid container spacing={1.25}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <DashboardIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">Dashboard</Typography>
          </Box>
          <Chip size="small" label={storageStatus} color={storageStatus === 'Stored' ? 'success' : 'error'} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">Odoo</Typography>
          </Box>
          {selectedIncident.odoo_ticket_state ? (
            <Chip
              size="small"
              label={selectedIncident.odoo_ticket_state}
              color={getOdooStateColor(selectedIncident.odoo_ticket_state) as any}
            />
          ) : selectedIncident.odoo_ticket_id ? (
            <Chip size="small" label="Linked" color="success" />
          ) : (
            <Chip size="small" label="No work order linked yet" variant="outlined" />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <WebhookIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">Webhook</Typography>
          </Box>
          <Chip size="small" label={webhookStatus} color={webhookStatus === 'Delivered' ? 'success' : 'default'} variant={webhookStatus === 'Delivered' ? 'filled' : 'outlined'} />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default RoutingIntegrationStatus;
